
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  Mail,
  MessageCircle,
  AlertTriangle,
  Building2,
  TrendingUp
} from 'lucide-react';
import { useClientSpecificEmailAnalytics } from '@/hooks/useClientSpecificEmailAnalytics';
import { ClientMailboxBounceModal } from './ClientMailboxBounceModal';
import { ClientMailboxReplyModal } from './ClientMailboxReplyModal';
import { ClientMailboxSentModal } from './ClientMailboxSentModal';

interface ClientBreakdownModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  metricType: 'sent' | 'replies' | 'bounces';
}

export function ClientBreakdownModal({ open, onOpenChange, metricType }: ClientBreakdownModalProps) {
  const { data: clientAnalytics, isLoading } = useClientSpecificEmailAnalytics();
  const [drillDownOpen, setDrillDownOpen] = useState(false);
  const [replyDrillDownOpen, setReplyDrillDownOpen] = useState(false);
  const [sentDrillDownOpen, setSentDrillDownOpen] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<{
    clientName: string;
    timePeriod: 'today' | 'yesterday' | 'last_7_days' | 'last_14_days' | 'last_30_days';
  } | null>(null);

  const handleBounceClick = (clientName: string, timePeriod: 'today' | 'yesterday' | 'last_7_days' | 'last_14_days' | 'last_30_days') => {
    setSelectedFilter({ clientName, timePeriod });
    setDrillDownOpen(true);
  };

  const handleReplyClick = (clientName: string, timePeriod: 'today' | 'yesterday' | 'last_7_days' | 'last_14_days' | 'last_30_days') => {
    setSelectedFilter({ clientName, timePeriod });
    setReplyDrillDownOpen(true);
  };

  const handleSentClick = (clientName: string, timePeriod: 'today' | 'yesterday' | 'last_7_days' | 'last_14_days' | 'last_30_days') => {
    setSelectedFilter({ clientName, timePeriod });
    setSentDrillDownOpen(true);
  };

  const getMetricConfig = () => {
    switch (metricType) {
      case 'sent':
        return {
          title: 'Emails Sent',
          icon: <Mail className="h-5 w-5 text-primary" />,
          color: 'text-primary',
          bgColor: 'bg-primary/5',
          getMetrics: (client: any) => ({
            today: client.today,
            yesterday: client.yesterday,
            last_7_days: client.last7Days,
            last_14_days: client.last14Days,
            last_30_days: client.last30Days,
          })
        };
      case 'replies':
        return {
          title: 'Email Replies',
          icon: <MessageCircle className="h-5 w-5 text-green-600" />,
          color: 'text-green-600',
          bgColor: 'bg-success/5',
          getMetrics: (client: any) => ({
            today: client.todayReplies,
            yesterday: client.yesterdayReplies,
            last_7_days: client.last7DaysReplies,
            last_14_days: client.last14DaysReplies,
            last_30_days: client.last30DaysReplies,
          })
        };
      case 'bounces':
        return {
          title: 'Email Bounces',
          icon: <AlertTriangle className="h-5 w-5 text-destructive" />,
          color: 'text-destructive',
          bgColor: 'bg-destructive/5',
          getMetrics: (client: any) => ({
            today: client.todayBounces,
            yesterday: client.yesterdayBounces,
            last_7_days: client.last7DaysBounces,
            last_14_days: client.last14DaysBounces,
            last_30_days: client.last30DaysBounces,
          })
        };
    }
  };

  const config = getMetricConfig();

  const calculateTotals = () => {
    if (!clientAnalytics) return null;
    
    return clientAnalytics.reduce((totals, client) => {
      const metrics = config.getMetrics(client);
      return {
        today: totals.today + metrics.today,
        yesterday: totals.yesterday + metrics.yesterday,
        last_7_days: totals.last_7_days + metrics.last_7_days,
        last_14_days: totals.last_14_days + metrics.last_14_days,
        last_30_days: totals.last_30_days + metrics.last_30_days,
      };
    }, {
      today: 0,
      yesterday: 0,
      last_7_days: 0,
      last_14_days: 0,
      last_30_days: 0,
    });
  };

  const totals = calculateTotals();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            {config.icon}
            Client Breakdown - {config.title}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Header Row */}
          <div className="grid grid-cols-6 gap-4 text-sm font-medium text-muted-foreground">
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Client
            </div>
            <div className="text-center">Today</div>
            <div className="text-center">Yesterday</div>
            <div className="text-center">Last 7 Days</div>
            <div className="text-center">Last 14 Days</div>
            <div className="text-center">Last 30 Days</div>
          </div>

          <Separator />

          {/* Loading State */}
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
            </div>
          )}

          {/* Client Rows */}
          {!isLoading && clientAnalytics && (
            <div className="space-y-3">
              {clientAnalytics.map((client) => {
                const metrics = config.getMetrics(client);
                return (
                  <div
                    key={client.clientId}
                    className={`grid grid-cols-6 gap-4 p-3 rounded-lg ${config.bgColor}`}
                  >
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary">{client.clientName}</Badge>
                    </div>
                    <div className={`text-center font-semibold ${config.color}`}>
                      {metricType === 'bounces' && metrics.today > 0 ? (
                        <button
                          onClick={() => handleBounceClick(client.clientName, 'today')}
                          className="hover:underline cursor-pointer"
                        >
                          {metrics.today.toLocaleString()}
                        </button>
                      ) : metricType === 'replies' && metrics.today > 0 ? (
                        <button
                          onClick={() => handleReplyClick(client.clientName, 'today')}
                          className="hover:underline cursor-pointer"
                        >
                          {metrics.today.toLocaleString()}
                        </button>
                      ) : metricType === 'sent' && metrics.today > 0 ? (
                        <button
                          onClick={() => handleSentClick(client.clientName, 'today')}
                          className="hover:underline cursor-pointer"
                        >
                          {metrics.today.toLocaleString()}
                        </button>
                      ) : (
                        metrics.today.toLocaleString()
                      )}
                    </div>
                    <div className={`text-center font-semibold ${config.color}`}>
                      {metricType === 'bounces' && metrics.yesterday > 0 ? (
                        <button
                          onClick={() => handleBounceClick(client.clientName, 'yesterday')}
                          className="hover:underline cursor-pointer"
                        >
                          {metrics.yesterday.toLocaleString()}
                        </button>
                      ) : metricType === 'replies' && metrics.yesterday > 0 ? (
                        <button
                          onClick={() => handleReplyClick(client.clientName, 'yesterday')}
                          className="hover:underline cursor-pointer"
                        >
                          {metrics.yesterday.toLocaleString()}
                        </button>
                      ) : metricType === 'sent' && metrics.yesterday > 0 ? (
                        <button
                          onClick={() => handleSentClick(client.clientName, 'yesterday')}
                          className="hover:underline cursor-pointer"
                        >
                          {metrics.yesterday.toLocaleString()}
                        </button>
                      ) : (
                        metrics.yesterday.toLocaleString()
                      )}
                    </div>
                    <div className={`text-center font-semibold ${config.color}`}>
                      {metricType === 'bounces' && metrics.last_7_days > 0 ? (
                        <button
                          onClick={() => handleBounceClick(client.clientName, 'last_7_days')}
                          className="hover:underline cursor-pointer"
                        >
                          {metrics.last_7_days.toLocaleString()}
                        </button>
                      ) : metricType === 'replies' && metrics.last_7_days > 0 ? (
                        <button
                          onClick={() => handleReplyClick(client.clientName, 'last_7_days')}
                          className="hover:underline cursor-pointer"
                        >
                          {metrics.last_7_days.toLocaleString()}
                        </button>
                      ) : metricType === 'sent' && metrics.last_7_days > 0 ? (
                        <button
                          onClick={() => handleSentClick(client.clientName, 'last_7_days')}
                          className="hover:underline cursor-pointer"
                        >
                          {metrics.last_7_days.toLocaleString()}
                        </button>
                      ) : (
                        metrics.last_7_days.toLocaleString()
                      )}
                    </div>
                    <div className={`text-center font-semibold ${config.color}`}>
                      {metricType === 'bounces' && metrics.last_14_days > 0 ? (
                        <button
                          onClick={() => handleBounceClick(client.clientName, 'last_14_days')}
                          className="hover:underline cursor-pointer"
                        >
                          {metrics.last_14_days.toLocaleString()}
                        </button>
                      ) : metricType === 'replies' && metrics.last_14_days > 0 ? (
                        <button
                          onClick={() => handleReplyClick(client.clientName, 'last_14_days')}
                          className="hover:underline cursor-pointer"
                        >
                          {metrics.last_14_days.toLocaleString()}
                        </button>
                      ) : metricType === 'sent' && metrics.last_14_days > 0 ? (
                        <button
                          onClick={() => handleSentClick(client.clientName, 'last_14_days')}
                          className="hover:underline cursor-pointer"
                        >
                          {metrics.last_14_days.toLocaleString()}
                        </button>
                      ) : (
                        metrics.last_14_days.toLocaleString()
                      )}
                    </div>
                    <div className={`text-center font-semibold ${config.color}`}>
                      {metricType === 'bounces' && metrics.last_30_days > 0 ? (
                        <button
                          onClick={() => handleBounceClick(client.clientName, 'last_30_days')}
                          className="hover:underline cursor-pointer"
                        >
                          {metrics.last_30_days.toLocaleString()}
                        </button>
                      ) : metricType === 'replies' && metrics.last_30_days > 0 ? (
                        <button
                          onClick={() => handleReplyClick(client.clientName, 'last_30_days')}
                          className="hover:underline cursor-pointer"
                        >
                          {metrics.last_30_days.toLocaleString()}
                        </button>
                      ) : metricType === 'sent' && metrics.last_30_days > 0 ? (
                        <button
                          onClick={() => handleSentClick(client.clientName, 'last_30_days')}
                          className="hover:underline cursor-pointer"
                        >
                          {metrics.last_30_days.toLocaleString()}
                        </button>
                      ) : (
                        metrics.last_30_days.toLocaleString()
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Totals Row */}
          {totals && (
            <>
              <Separator />
              <div className="grid grid-cols-6 gap-4 p-3 bg-muted/30 rounded-lg border">
                <div className="flex items-center gap-2 font-semibold">
                  <TrendingUp className="h-4 w-4" />
                  Total
                </div>
                <div className={`text-center font-bold ${config.color}`}>
                  {totals.today.toLocaleString()}
                </div>
                <div className={`text-center font-bold ${config.color}`}>
                  {totals.yesterday.toLocaleString()}
                </div>
                <div className={`text-center font-bold ${config.color}`}>
                  {totals.last_7_days.toLocaleString()}
                </div>
                <div className={`text-center font-bold ${config.color}`}>
                  {totals.last_14_days.toLocaleString()}
                </div>
                <div className={`text-center font-bold ${config.color}`}>
                  {totals.last_30_days.toLocaleString()}
                </div>
              </div>
            </>
          )}

          {/* Empty State */}
          {!isLoading && (!clientAnalytics || clientAnalytics.length === 0) && (
            <div className="text-center py-8 text-muted-foreground">
              No client data available
            </div>
          )}
        </div>
      </DialogContent>

      <ClientMailboxBounceModal
        open={drillDownOpen}
        onOpenChange={setDrillDownOpen}
        filter={selectedFilter}
      />

      <ClientMailboxReplyModal
        open={replyDrillDownOpen}
        onOpenChange={setReplyDrillDownOpen}
        filter={selectedFilter}
      />

      <ClientMailboxSentModal
        open={sentDrillDownOpen}
        onOpenChange={setSentDrillDownOpen}
        filter={selectedFilter}
      />
    </Dialog>
  );
}
